import { GridColDef } from "@mui/x-data-grid-pro"
import { StudentAcademicStatusDTO } from "../../studentAcademicStatus.dto"
import { CSAT_COLUMNS } from "./csat.columns"
import { ACTIVITY_COLUMNS } from "./activity.columns"
import { ATTENTION_COLUMN } from "./attention.columns"
import { ColumnKeys } from "./column.keys"
import { CONTACT_DETAILS_COLUMNS } from "./contact.details.columns"
import {
  ENROLLMENT_COLUMN,
  OUTCOME_COLUMN,
  REGISTRATION_COLUMNS,
  START_DATE_COLUMN,
} from "./registration.details.columns"
import { ProgramStatus } from "@features/students-tab/studentsTab.slice"

// The three tabs share one column pool but not one presentation: alumni rows
// must not carry intervention styling, so for the ended tab the CSAT/NPS
// red-flag classes are stripped (the scores stay, as exit records).
// Pool order is display order: the default-visible columns (Name, Enrollment,
// scores, Starts, Mentor) lead; the chooser-only singles (School, Cohort,
// Class, Language) trail.
export function buildStudentsTableColumns(
  programStatus: ProgramStatus,
): GridColDef<StudentAcademicStatusDTO>[] {
  const columns: GridColDef<StudentAcademicStatusDTO>[] = (
    [
      ...CONTACT_DETAILS_COLUMNS,
      ENROLLMENT_COLUMN,
      ...CSAT_COLUMNS,
      ...ACTIVITY_COLUMNS,
      START_DATE_COLUMN,
      {
        field: ColumnKeys.LAST_INTERVIEW_SCORE,
        headerName: "Last mock interview score",
        width: 150,
        valueGetter: (value, studentRow: StudentAcademicStatusDTO) => {
          return studentRow.status.proficiency.lastInterviewScore
        },
      },
      {
        field: ColumnKeys.MENTOR,
        headerName: "Mentor",
        flex: 0.8,
        minWidth: 130,
        valueGetter: (value, studentRow: StudentAcademicStatusDTO) => {
          return studentRow.staff?.mentor?.name
        },
      },
      ...REGISTRATION_COLUMNS,
    ] as GridColDef<StudentAcademicStatusDTO>[]
  ).map((column) => ({ ...column, headerAlign: "left" }))
  if (programStatus === "active") {
    // Attention sits right after Last Progress: the chips explain the
    // "Needs attention" pill, so they belong in the intervention viewport.
    // Active only — on Upcoming there is no data yet, on Ended no program
    // is running, so the column would be structurally empty or dishonest.
    return columns.flatMap((column) =>
      column.field === ColumnKeys.LAST_LEARNING_ACTIVITY
        ? [column, { ...ATTENTION_COLUMN, headerAlign: "left" as const }]
        : [column],
    )
  }
  if (programStatus !== "ended") {
    return columns
  }
  // Outcome sits right after Enrollment — the exit grouping must be visible
  // without scrolling, not appended past the viewport. Alumni rows carry no
  // intervention styling: score red-flag classes are stripped, and so is the
  // Last Progress stall red (every ended program "stalled" by definition).
  return columns
    .map((column) =>
      column.field === ColumnKeys.LAST_CSAT_SCORE ||
      column.field === ColumnKeys.LAST_NPS_SCORE ||
      column.field === ColumnKeys.LAST_LEARNING_ACTIVITY
        ? { ...column, cellClassName: undefined }
        : column,
    )
    .flatMap((column) =>
      column.field === ColumnKeys.ENROLLMENT
        ? [column, { ...OUTCOME_COLUMN, headerAlign: "left" as const }]
        : [column],
    )
}
